export const PLACEHOLDER = "\u2014";

export const DEFAULT_EDI_FILTERS = Object.freeze({
  cust_code: "",
  partner_code: "",
  direction: "",
  transaction_type: "",
  processing_status: "",
  has_errors: "",
  has_997_draft: "",
  date_from: "",
  date_to: "",
  date_basis: "event",
  sort: "newest",
  source_type: "",
  backfill_run_uuid: "",
  assessment_status: "",
  correlation_state: "",
  acknowledgement_outcome: "",
  business_status: "",
  has_transport_evidence: "",
  has_assessment: "",
});

export const EMPTY_HISTORICAL_CONTEXT = Object.freeze({
  is_historical: false,
  backfill_run: null,
  sources: Object.freeze([]),
  transport_evidence: Object.freeze([]),
  assessments_as_source: Object.freeze([]),
  assessments_as_acknowledgement: Object.freeze([]),
});

export const asArray = (value) => (Array.isArray(value) ? value : []);

export const shown = (value) =>
  value === null || value === undefined || value === "" ? PLACEHOLDER : String(value);

export function formatTimestamp(value) {
  if (!value) return PLACEHOLDER;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? shown(value) : date.toLocaleString();
}

export function readableCode(value) {
  if (!value) return PLACEHOLDER;
  const known = {
    HISTORICAL_BACKFILL: "Historical Backfill",
    INGESTED_AT: "Ingested At",
    MATCHED: "Matched",
    PARTIAL_TRANSACTION_ACK: "Partial",
    NO_997_EVIDENCE: "No 997 Evidence",
    PENDING_CORRELATION: "Pending Correlation",
    ACK_NOT_FOUND: "Acknowledgement Not Found",
    SOURCE_NOT_FOUND: "Source Not Found",
    LEGACY_SOURCE_OUTSIDE_PORTPILOT_SCOPE: "Legacy Source Outside PortPilot Scope",
    SOURCE_EXCLUDED_PENDING_BUSINESS_REVIEW: "Source Excluded — Business Review",
    AMBIGUOUS_ACK: "Ambiguous Acknowledgement",
    AMBIGUOUS_SOURCE: "Ambiguous Source",
    PARSED: "Parsed",
    PARSE_FAILED: "Parse Failed",
    INBOUND: "Inbound",
    OUTBOUND: "Outbound",
    VERIFIED: "Verified",
    MISSING: "Missing",
    MISMATCH: "Mismatch",
  };
  if (known[value]) return known[value];
  return String(value)
    .toLowerCase()
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function eventTimestamp(row = {}) {
  return row.event_at ?? row.received_at ?? null;
}

export function importedTimestamp(row = {}) {
  return row.imported_at ?? row.received_at ?? null;
}

export function historicalContext(detail = {}) {
  const value = detail.historical_context;
  if (!value || typeof value !== "object") {
    return {
      is_historical: false,
      backfill_run: null,
      sources: [],
      transport_evidence: [],
      assessments_as_source: [],
      assessments_as_acknowledgement: [],
    };
  }
  return {
    is_historical: Boolean(value.is_historical),
    backfill_run: value.backfill_run ?? null,
    sources: asArray(value.sources),
    transport_evidence: asArray(value.transport_evidence),
    assessments_as_source: asArray(value.assessments_as_source),
    assessments_as_acknowledgement: asArray(value.assessments_as_acknowledgement),
  };
}

export function buildEdiMessageQuery(filters, offset, limit = 50) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    date_basis: filters?.date_basis || "event",
    sort: filters?.sort || "newest",
  });
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (["date_basis", "sort"].includes(key)) return;
    if (value !== "" && value !== null && value !== undefined) params.set(key, String(value));
  });
  return params.toString();
}

export function safeDisplayValue(value) {
  if (Array.isArray(value)) return value.map(safeDisplayValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key, item]) =>
        !/(raw|payload|x12|segment.?text)/i.test(key) &&
        !(item instanceof Uint8Array) &&
        !(typeof Blob !== "undefined" && item instanceof Blob)
      )
      .map(([key, item]) => [key, safeDisplayValue(item)])
  );
}

export function ediReadErrorMessage(error, fallback = "Unable to load EDI messages.") {
  if (error?.status === 400) return "The EDI filters are invalid. Review the values and try again.";
  if (error?.status === 401) return "Please sign in to access EDI resources.";
  if (error?.status === 403) return "You do not have permission to view this EDI resource.";
  if (error?.status === 503) return "The EDI service is temporarily unavailable.";
  return error?.message || fallback;
}

export const BACKEND_CONTRACT_COMMIT = "aca187169b51ccfce3213698bcdf5c5a237b8a3d";
export const BACKEND_CONTRACT_FIXTURE_SHA256 = "45ff13d0dc25f78f11f62deeaf2ed0ac2399db5d2ce369305c7f7baa4a84536d";
